import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, Coffee, Headphones, Info, Moon, Users, type LucideIcon } from "lucide-react";

import type { OfficeStatus } from "../api/types";
import { STATUS_HEX, STATUS_LABEL_KEYS } from "../lib/format";
import { Avatar } from "./Avatar";

/**
 * Small HomeWork atoms from the design (weco2/homework-data.jsx): the page
 * head, empty/error state, skeleton, status pill and the status-ringed avatar.
 * Styling lives in `homework.css` under the `.hw` scope.
 */

/** The design's HW_ST icon per status, drawn in the avatar's status badge. */
export const STATUS_ICON: Record<OfficeStatus, LucideIcon> = {
  available: Check,
  focus: Headphones,
  meeting: Users,
  break: Coffee,
  away: Clock,
  offline: Moon,
};

/** Low-alpha wash of each status hue behind a pill (design HW_ST.wash). */
const STATUS_WASH: Record<OfficeStatus, string> = {
  available: "rgba(16,185,129,.14)",
  focus: "rgba(139,92,246,.14)",
  meeting: "rgba(245,158,11,.16)",
  break: "rgba(14,165,233,.14)",
  away: "rgba(100,116,139,.18)",
  offline: "rgba(148,163,184,.22)",
};

/**
 * Pill ink per status. The design hardcodes the deep shade, which drops under
 * 4.5:1 on dark glass, so the pale one takes over in dark (same rule as
 * `STATUS_CLASSES` in lib/format).
 */
const STATUS_INK: Record<OfficeStatus, string> = {
  available: "text-emerald-700 dark:text-emerald-300",
  focus: "text-violet-700 dark:text-violet-300",
  meeting: "text-amber-700 dark:text-amber-300",
  break: "text-sky-700 dark:text-sky-300",
  away: "text-slate-600 dark:text-slate-300",
  offline: "text-slate-600 dark:text-slate-300",
};

export function HwHead({
  title,
  explainer,
  right,
}: {
  title: string;
  explainer?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="hw-title">{title}</h1>
        {explainer && <p className="hw-expl">{explainer}</p>}
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  );
}

export function HwState({
  icon: Icon = Info,
  title,
  line,
  action,
  tone,
  bare,
}: {
  icon?: LucideIcon;
  title: string;
  line?: string;
  action?: ReactNode;
  tone?: "error";
  /** Render without its own glass card (when already inside one). */
  bare?: boolean;
}) {
  return (
    <div
      className={`${bare ? "" : "hw-glass hw-card"} hw-fade flex flex-col items-center gap-2 py-12 text-center`}
      role={tone === "error" ? "alert" : undefined}
    >
      <span
        className={`mb-1 flex size-12 items-center justify-center rounded-full ${
          tone === "error"
            ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
            : "hw-tile-brand"
        }`}
      >
        <Icon size={22} />
      </span>
      <p className="hw-ct">{title}</p>
      {line && (
        <p className="hw-muted m-0" style={{ maxWidth: "42ch" }}>
          {line}
        </p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function HwSkeleton({ rows = 3, cards = 4 }: { rows?: number; cards?: number }) {
  const { t } = useTranslation();
  return (
    <div className="hw-page" aria-busy="true" aria-label={t("homework.common.loading", { defaultValue: "Loading" })}>
      <div className="hw-sk" style={{ height: 34, width: 220 }} />
      <div className="hw-sk" style={{ height: 16, width: "min(420px,80%)" }} />
      <div className="hw-g4">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="hw-sk" style={{ height: 104, borderRadius: 24 }} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="hw-sk" style={{ height: 140, borderRadius: 24 }} />
      ))}
    </div>
  );
}

/** Design HwStatusPill: hue wash, coloured dot, the status name. */
export function HwStatusPill({
  status,
  message,
  className = "",
}: {
  status: OfficeStatus;
  message?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span
      className={`hw-pill ${STATUS_INK[status]} ${className}`}
      style={{ background: STATUS_WASH[status] }}
    >
      <span className="size-2 rounded-full" style={{ background: STATUS_HEX[status] }} aria-hidden />
      {t(STATUS_LABEL_KEYS[status])}
      {message ? <span className="font-medium opacity-75">· {message}</span> : null}
    </span>
  );
}

/**
 * The design's HwAvatar: the generated (or uploaded) avatar with an optional
 * status ring and a status badge in the corner. The badge is decorative; the
 * status is always also said in words next to it.
 */
export function HwAvatar({
  name,
  id,
  url,
  status,
  size = 36,
  ring = true,
  badge = false,
}: {
  name: string;
  id: string;
  url?: string;
  status?: OfficeStatus;
  size?: number;
  ring?: boolean;
  badge?: boolean;
}) {
  const color = status ? STATUS_HEX[status] : undefined;
  const BadgeIcon = status ? STATUS_ICON[status] : null;
  const badgeSize = Math.max(14, size * 0.4);
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      <span
        className="inline-flex rounded-full"
        style={{
          boxShadow: ring && color ? `0 0 0 2px var(--surface-page), 0 0 0 4px ${color}` : "none",
        }}
      >
        <Avatar name={name} id={id} url={url} size={size} />
      </span>
      {badge && BadgeIcon && color && (
        <span
          aria-hidden
          className="absolute flex items-center justify-center rounded-full text-white"
          style={{
            insetInlineEnd: -3,
            bottom: -3,
            width: badgeSize,
            height: badgeSize,
            background: color,
            boxShadow: "0 0 0 2px var(--surface-page)",
          }}
        >
          <BadgeIcon size={Math.max(9, size * 0.24)} strokeWidth={3} />
        </span>
      )}
    </span>
  );
}
